import Button from 'components/ButtonDeprecated';
import Fieldset from 'components/Fieldset';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import * as React from 'react';
import { useState, useEffect } from 'react';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

type BirthdateMutation = {
  variables: {
    input?: {
      userUuid?: string;
      birthdate?: string;
    };
  };
};

type UpdateBirthdateResponse = {
  data?: {
    updateBirthdate?: {
      error?: {
        message?: string;
      } | null;
    };
  } | null;
};

export type EditBirthdateFormProps = {
  mutate: (dict: BirthdateMutation) => Promise<UpdateBirthdateResponse>;
  userUuid?: string;
  currentBirthdate?: string;
  onBirthdateUpdated?: () => void;
  onRequestHide?: () => void;
};

type Props = EditBirthdateFormProps;

const formatBirthdateForInput = (birthdate?: string): string | null => {
  if (!birthdate) return null;
  // Parse ISO date string directly to avoid timezone issues
  const match = birthdate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const normalizeBirthdateFormat = (birthdate: string): string => {
  if (!birthdate || birthdate.trim() === '') return '';
  // Convert MM-DD-YYYY to MM/DD/YYYY
  return birthdate.replace(/-/g, '/');
};

const validateDate = (value: string): boolean => {
  if (!value || value.trim() === '') return false;
  // Accept both MM/DD/YYYY and MM-DD-YYYY formats
  const mmddyyyyPattern = /^(0[1-9]|1[0-2])[\/\-](0[1-9]|[12]\d|3[01])[\/\-](\d{4})$/;
  const match = value.trim().match(mmddyyyyPattern);
  if (!match) return false;
  const [, month, day, year] = match;
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);
  // Reject future dates (birthdates cannot be in the future)
  if (yearNum > new Date().getFullYear()) return false;
  // Validate that the date is actually valid (e.g., catches Feb 30, invalid leap years)
  const date = new Date(yearNum, monthNum - 1, dayNum);
  return (
    date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum
  );
};

function EditBirthdateForm(props: Props): JSX.Element {
  const [canSubmit, setCanSubmit] = useState(false);
  const [birthdateValue, setBirthdateValue] = useState(
    formatBirthdateForInput(props.currentBirthdate) || ''
  );

  useEffect(() => {
    const formattedBirthdate = formatBirthdateForInput(props.currentBirthdate) || '';
    setBirthdateValue(formattedBirthdate);
    setCanSubmit(false);
  }, [props.currentBirthdate]);

  const saveBirthdateAndRefresh = (birthdate: string) => {
    const reset = () => {
      setCanSubmit(false);
    };

    const onSuccess = () => {
      ToastActions.showSuccessToast('Changes saved');
      if (props.onBirthdateUpdated) {
        props.onBirthdateUpdated();
      }
    };

    const onFailure = (error: any) => {
      ToastActions.showAPIError(error);
      ToastActions.showDefaultErrorToast();
      props.onRequestHide && props.onRequestHide();
    };

    if (!props.mutate) {
      console.error('EditBirthdateForm: mutate function is required but was not provided');
      onFailure(new Error('Mutation function is not available'));
      return;
    }

    const { mutate, userUuid } = props;

    // Normalize birthdate format (convert MM-DD-YYYY to MM/DD/YYYY) before saving
    const normalizedBirthdate = normalizeBirthdateFormat(birthdate.trim());
    mutate({
      variables: {
        input: {
          userUuid,
          birthdate: normalizedBirthdate,
        },
      },
    })
      .then((response: any) => {
        const error = response?.data?.updateBirthdate?.error;
        if (error) {
          throw new Error(error.message || 'An error occurred while updating birthdate');
        }
        reset();
      })
      .then(onSuccess, onFailure);
  };

  const currentBirthdateFormatted = formatBirthdateForInput(props.currentBirthdate);

  return (
    <form
      data-test="edit-birthdate-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && validateDate(birthdateValue)) {
          saveBirthdateAndRefresh(birthdateValue);
        }
      }}
    >
      <Fieldset>
        {currentBirthdateFormatted && (
          <FormElement
            data-test="current-birthdate-display"
            label="Current birthdate"
            element={Input}
            elementProps={{
              value: currentBirthdateFormatted,
              disabled: true,
              type: 'text',
            }}
          />
        )}
        <Row size="xxsmall">
          <Text align="left">
            Enter the birthdate in MM/DD/YYYY or MM-DD-YYYY format (e.g., 01/15/2010 or 01-15-2010).
          </Text>
        </Row>
        <FormElement
          data-test="birthdate-input"
          label="Birthdate"
          onValidChange={(e) => {
            const value = e.target.value;
            const isValid = validateDate(value);
            const isChanged = value !== currentBirthdateFormatted;
            if (isValid && isChanged) {
              setCanSubmit(true);
            } else {
              setCanSubmit(false);
            }
            setBirthdateValue(value);
          }}
          onInvalidChange={(e) => {
            // Update state even when input is invalid (e.g., partial dates)
            const value = e.target.value;
            setBirthdateValue(value);
            setCanSubmit(false);
          }}
          validate={(e) => {
            const value = e.target.value;
            if (value && !validateDate(value)) {
              throw new Error('Please enter a valid date in MM/DD/YYYY or MM-DD-YYYY format');
            }
          }}
          element={Input}
          elementProps={{
            placeholder: 'MM/DD/YYYY or MM-DD-YYYY',
            value: birthdateValue,
            type: 'text',
          }}
        />
        <Button size="small" type="submit" disabled={!canSubmit} data-test="save-birthdate-button">
          Save
        </Button>
      </Fieldset>
    </form>
  );
}

export default EditBirthdateForm;
